// KD-3817 — when a body or a query fails the API's validation, the 400 only
// says "Invalid body, check 'errors' property for more info." and keeps the
// actual reasons in `errors` (class-validator ValidationError[], or plain
// strings). Screens show `message`, so the user never learned which field was
// refused.

// useError drops any backend message longer than this.
const MAX_MESSAGE_LENGTH = 200;
const MAX_REASONS = 3;

// "hotelRoomContracts.0.periods.2.startAt" is the API's own naming: spell it
// the way the screen does, with words and 1-based rows.
const humanizePath = (path) => {
  const label = path
    .split('.')
    .map((segment) =>
      /^\d+$/.test(segment)
        ? `#${Number(segment) + 1}`
        : segment
            .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
            .replace(/_/g, ' ')
            .toLowerCase(),
    )
    .join(' ');

  return label.charAt(0).toUpperCase() + label.slice(1);
};

const collectReasons = (errors, parentPath = '') =>
  (errors ?? []).flatMap((error) => {
    if (typeof error === 'string') {
      return [error];
    }

    const path = [parentPath, error?.property].filter(Boolean).join('.');
    const label = humanizePath(path);
    // class-validator starts its messages with the bare property name; swap
    // it for the full path so a nested field can be told apart from its
    // siblings ("Periods #3 start at must be a string").
    const own = Object.values(error?.constraints ?? {}).map((reason) =>
      error?.property && reason.startsWith(`${error.property} `)
        ? `${label}${reason.slice(error.property.length)}`
        : `${label}: ${reason}`,
    );

    return [...own, ...collectReasons(error?.children, path)];
  });

/** The reasons the API gave for refusing a request, flattened to sentences. */
export const getValidationErrorReasons = (error) => {
  const data = error?.response?.data;

  if (error?.response?.status !== 400 || !Array.isArray(data?.errors)) {
    return [];
  }

  return collectReasons(data.errors);
};

/**
 * axios response interceptor step: replaces the generic "check 'errors'
 * property" message of a validation 400 with its actual reasons, so every
 * screen that displays `response.data.message` names the refused fields.
 */
export const exposeValidationErrors = (error) => {
  const reasons = getValidationErrorReasons(error);

  if (reasons.length === 0) {
    return error;
  }

  const message = [
    ...reasons.slice(0, MAX_REASONS),
    ...(reasons.length > MAX_REASONS ? ['…'] : []),
  ].join(' · ');

  error.response.data.message =
    message.length > MAX_MESSAGE_LENGTH
      ? `${message.slice(0, MAX_MESSAGE_LENGTH - 1)}…`
      : message;

  return error;
};
